import { cn } from '@/lib/cn';
import type { ProposalDecision } from '@mdu-invoicing/shared';

interface ProposalDecisionCellProps {
  decision: ProposalDecision | null;
  comment: string;
  /** True when the staff user may edit here (staff role + current month). Drives whether the
   *  editor (toggle + input) or a read-only badge/text is shown. */
  editable: boolean;
  /** True to temporarily lock the editor without switching to read-only — a bulk save is
   *  running, or the row has no contractor (never proposal-decidable). */
  disabled: boolean;
  mduId: string;
  onDecision: (decision: ProposalDecision) => void;
  onComment: (comment: string) => void;
  onCommentBlur: () => void;
}

/**
 * The two Unifiber-proposal cells of the eligibles grid (staff only): the **Décision UF**
 * toggle (Accepté / Refusé — click the active one again to clear) and the **Commentaire
 * décision** input.
 * Returns a fragment of two `<td>`s so it drops straight into the row, right after the
 * provider eligibility columns. Extracted from the former Validation screen (Lot 3 merge).
 */
export function ProposalDecisionCell({
  decision,
  comment,
  editable,
  disabled,
  mduId,
  onDecision,
  onComment,
  onCommentBlur,
}: ProposalDecisionCellProps) {
  return (
    <>
      {/* Décision UF */}
      <td className="px-4 py-2.5">
        {editable ? (
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            {(['ACCEPTED', 'REFUSED'] as ProposalDecision[]).map((p) => (
              <button
                key={p}
                type="button"
                disabled={disabled}
                aria-pressed={decision === p}
                aria-label={`${p === 'ACCEPTED' ? 'Accepter' : 'Refuser'} ${mduId}`}
                onClick={() => onDecision(p)}
                className={cn(
                  'px-2.5 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  decision === p
                    ? p === 'ACCEPTED'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-rose-500 text-white'
                    : 'bg-white text-slate-500 hover:bg-slate-50',
                )}
              >
                {p === 'ACCEPTED' ? 'Accepté' : 'Refusé'}
              </button>
            ))}
          </div>
        ) : decision ? (
          <span
            className={cn(
              'inline-flex w-fit rounded-md border px-2 py-0.5 text-xs font-semibold',
              decision === 'ACCEPTED'
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'border-rose-300 bg-rose-50 text-rose-700',
            )}
          >
            {decision === 'ACCEPTED' ? 'Accepté' : 'Refusé'}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      {/* Commentaire décision (ex-"Comm. UF" — Gerd 2026-08) */}
      <td className="px-4 py-2.5">
        {editable ? (
          <input
            value={comment}
            disabled={disabled}
            aria-label={`Commentaire décision ${mduId}`}
            onChange={(e) => onComment(e.target.value)}
            onBlur={onCommentBlur}
            placeholder="Optionnel"
            className="w-48 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 placeholder:text-slate-400 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20 disabled:bg-slate-50"
          />
        ) : (
          <span className="text-slate-500">{comment || '—'}</span>
        )}
      </td>
    </>
  );
}
